import { Group, Rect, Circle, Text } from 'react-konva';
import { getTableLayout, STATUS_COLOR, STATUS_COLOR_STRONG, CANVAS_WIDTH, CANVAS_HEIGHT, GRID_SIZE } from '../canvasConstants';

export const TableGroup = ({
  table,
  enableDrag,
  onDragEnd,
  onHover,
  onClick,
  solid = false,       // stronger colors, used by the read-only reservation plan
  labelFontSize = 12,
  showSeats = false,   // second line "4 pers."
  subLabel,            // third line, e.g. customer name
}) => {
  const { id, shape, persons, positionX, positionY, status, reference } = table;
  const layout = getTableLayout(shape, persons);
  const dim = { w: layout.w, h: layout.h };
  const top = layout.top;

  const cx = positionX * CANVAS_WIDTH;
  const cy = positionY * CANVAS_HEIGHT;

  const handleDragEnd = (e) => {
    const newX = (e.target.x() + dim.w / 2) / CANVAS_WIDTH;
    const newY = (e.target.y() + dim.h / 2) / CANVAS_HEIGHT;
    onDragEnd(id, newX, newY);
  };

  const palette = solid ? STATUS_COLOR_STRONG : STATUS_COLOR;
  const fill = palette[status] ?? palette.available;

  // label lines, vertically centered on the tabletop
  const smallFont = Math.max(10, Math.round(labelFontSize * 0.72));
  const lines = [
    { text: reference, size: labelFontSize, bold: true },
    showSeats && persons ? { text: `${persons} pers.`, size: smallFont } : null,
    subLabel ? { text: subLabel, size: smallFont } : null,
  ].filter(Boolean);
  const lineGap = 2;
  const blockHeight = lines.reduce((sum, l) => sum + l.size + lineGap, -lineGap);
  // circles have less usable width near the top/bottom
  const textPad = top.circle ? top.w * 0.12 : 3;
  let lineY = top.y + (top.h - blockHeight) / 2;

  return (
    <Group
      id={String(id)}
      x={cx - dim.w / 2}
      y={cy - dim.h / 2}
      draggable={enableDrag}
      dragBoundFunc={(pos) => ({
        x: Math.max(0, Math.min(CANVAS_WIDTH  - dim.w, Math.round(pos.x / GRID_SIZE) * GRID_SIZE)),
        y: Math.max(0, Math.min(CANVAS_HEIGHT - dim.h, Math.round(pos.y / GRID_SIZE) * GRID_SIZE)),
      })}
      onDragEnd={handleDragEnd}
      onMouseEnter={onHover ? () => onHover(table) : undefined}
      onMouseLeave={onHover ? () => onHover(null)  : undefined}
      onClick={onClick}
    >
      {/* transparent hit area so the gaps between chairs are clickable too */}
      <Rect width={dim.w} height={dim.h} fill="rgba(0,0,0,0)" />
      {layout.chairs.map((c, i) => (
        <Rect
          key={i}
          x={c.cx}
          y={c.cy}
          width={c.w}
          height={c.h}
          offsetX={c.w / 2}
          offsetY={c.h / 2}
          rotation={c.rotation}
          cornerRadius={3}
          fill={fill}
          opacity={0.55}
          listening={false}
        />
      ))}
      {top.circle ? (
        <Circle
          x={top.x + top.w / 2}
          y={top.y + top.h / 2}
          radius={top.w / 2}
          fill={fill}
          stroke="rgba(0,0,0,0.25)"
          strokeWidth={1.5}
          shadowColor="rgba(0,0,0,0.3)"
          shadowBlur={enableDrag ? 6 : 0}
        />
      ) : (
        <Rect
          x={top.x}
          y={top.y}
          width={top.w}
          height={top.h}
          cornerRadius={6}
          fill={fill}
          stroke="rgba(0,0,0,0.25)"
          strokeWidth={1.5}
          shadowColor="rgba(0,0,0,0.3)"
          shadowBlur={enableDrag ? 6 : 0}
        />
      )}
      {lines.map((line, i) => {
        const y = lineY;
        lineY += line.size + lineGap;
        return (
          <Text
            key={i}
            x={top.x + textPad}
            y={y}
            width={top.w - textPad * 2}
            text={line.text ?? ''}
            fontSize={line.size}
            fontStyle={line.bold ? 'bold' : 'normal'}
            fill="#fff"
            align="center"
            wrap="none"
            ellipsis
            listening={false}
          />
        );
      })}
    </Group>
  );
};
